import { autorun, makeAutoObservable, runInAction } from "mobx";
import { CompanyroleId } from "../../types/CompanyInfo";
import { isBoardMember, isDeputyBoardMember, isObserver } from "../../utils/Roles";
import {
  AttachmentType,
  CleanupFunction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DecisionTextModel,
  deleteAttachmentEvent,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  isLanguageChanged,
  mapDecisionTextModel,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalizedDecisionTextsToModel,
  ProjectDocumentStatus,
  UpdateBoardMeetingRecipientItem,
  UpdateBoardMeetingRecipientsResultEvent,
  uploadAttachmentsEvent,
  WithAttachmentSupport,
  WithInitialization,
} from "../common/commonDomain";
import { toLocalDateISOString } from "../../utils/Dates";
import { identity } from "lodash";
import {
  boardMeetingParticipantsLabel,
  ExtraParticipant,
  getSelectedBoardRoleIds,
  getSortedBoard,
  prepProjectCompanyRoleModel,
  ProjectCompanyRole,
} from "../common/commonBoardMeetingDomain";
import { newUuid } from "../../utils/uuid";
import { DocumentLanguage, isOk, LocalizedText } from "../../common/common";
import { FileUploadConfig, UploadedAttachment } from "../../fileUpload/fileUploadDomain";
import { formatDateAndTime } from "../../i18n/dateFormat";
import {
  openNotifyNoticeChangeDialogAction,
  openSendNoticeDialogAction,
} from "../summary/summaryDomain";
import { Language } from "../../i18n/languages";
import { DocumentType } from "../../common/documents";
import { MeetingForm, MeetingPlace } from "../../meetings/meetingsDomain";
import {
  createEmailField,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "../../common/commonForms";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";

export enum BoardMeetingForm {
  PHYSICAL = "physical_meeting",
  ELECTRONIC = "electronic",
  HYBRID = "hybrid",
  CIRCULATION = "circulation",
}

export const mapBoardMeetingFormToMeetingForm = (f: BoardMeetingForm): MeetingForm => {
  switch (f) {
    case BoardMeetingForm.PHYSICAL:
      return MeetingForm.PHYSICAL;
    case BoardMeetingForm.ELECTRONIC:
      return MeetingForm.ELECTRONIC;
    case BoardMeetingForm.HYBRID:
      return MeetingForm.HYBRID;
    case BoardMeetingForm.CIRCULATION:
      return MeetingForm.CIRCULATION;
  }
};

export enum NoticeBoardMeetingLinkOption {
  linkGiven = "linkGiven",
  linkGivenLater = "linkGivenLater",
}

export type NoticeOfBoardMeeting = {
  type: DocumentType.notice_board_meeting;
  dateNoticeBoardMeeting?: string;
  documentLanguage: Language[];
  attachments: UploadedAttachment[];
  meetingForm: BoardMeetingForm;
  meetingPlace: MeetingPlace;
  meetingLinkOption: NoticeBoardMeetingLinkOption;
  inlineAddress: string;
  inlineMeetingLink: string;
  board: ProjectCompanyRole[];
  participants: CompanyroleId[];
  extraParticipants: ExtraParticipant[];
};

export const noticeOfBoardMeeting = (
  data: Partial<NoticeOfBoardMeeting> = {},
): NoticeOfBoardMeeting => ({
  type: DocumentType.notice_board_meeting,
  dateNoticeBoardMeeting: data.dateNoticeBoardMeeting,
  documentLanguage: data.documentLanguage ?? [Language.NO],
  attachments: data.attachments ?? [],
  meetingForm: data.meetingForm ?? BoardMeetingForm.HYBRID,
  meetingPlace: data.meetingPlace ?? MeetingPlace.companysOffice,
  meetingLinkOption: data.meetingLinkOption ?? NoticeBoardMeetingLinkOption.linkGivenLater,
  inlineAddress: data.inlineAddress ?? "",
  inlineMeetingLink: data.inlineMeetingLink ?? "",
  board: data.board ?? [],
  participants: data.participants ?? [],
  extraParticipants: data.extraParticipants ?? [],
});

export class NoticeOfBoardMeetingForm implements WithInitialization, WithAttachmentSupport {
  readonly type = DocumentType.notice_board_meeting;
  context: DocumentContext<NoticeOfBoardMeeting>;
  dispatch: DbotEventDispatcher;
  meetingLinkOption: RadioFieldModel<NoticeBoardMeetingLinkOption>;
  inlineMeetingLink: StringFieldModel;
  dateNoticeBoardMeeting: LocalDateFieldModel;
  documentLanguage: RadioFieldModel<DocumentLanguage>;
  decisionTextModels: DecisionTextModel[];
  attachments: Map<string, UploadedAttachment> = new Map<string, UploadedAttachment>();
  board: ProjectCompanyRole[];
  selectedBoardMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedDeputyMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedObservers: OptionFieldModel<ProjectCompanyRole>;
  extraParticipants: ExtraParticipant[];
  extraParticipantFormModel?: { nameField: StringFieldModel; emailField: StringFieldModel };

  isUploadingAttachments?: boolean;

  constructor(context: DocumentContext<NoticeOfBoardMeeting>, dispatch: DbotEventDispatcher) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.context = context;
    this.dispatch = dispatch;
    const data = context.documentData;

    this.dateNoticeBoardMeeting = new LocalDateFieldModel({
      value: data?.dateNoticeBoardMeeting ?? toLocalDateISOString(Date.now()),
    });
    this.documentLanguage = documentLanguageFieldModel({
      orgNo: context.orgNo,
      value: mapLocalesToDocumentLanguage(data?.documentLanguage),
    });
    this.attachments = new Map(data?.attachments?.map((a) => [a.id, a]));
    this.meetingLinkOption = createMeetingLinkOptionField(
      data?.meetingLinkOption ?? NoticeBoardMeetingLinkOption.linkGivenLater,
    );
    this.inlineMeetingLink = new StringFieldModel({ value: data?.inlineMeetingLink ?? "" });

    this.board = getSortedBoard(context);
    this.extraParticipants = data?.extraParticipants ?? [];

    this.selectedBoardMembers = prepProjectCompanyRoleModel({
      options: this.boardMembers,
      selectedIds: data?.participants,
      defaultValue: this.boardMembers,
    });

    this.selectedDeputyMembers = prepProjectCompanyRoleModel({
      options: this.deputyMembers,
      selectedIds: data?.participants,
      defaultValue: [],
    });

    this.selectedObservers = prepProjectCompanyRoleModel({
      options: this.observers,
      selectedIds: data?.participants,
      defaultValue: [],
    });

    this.decisionTextModels = mapLocalizedDecisionTextsToModel(
      context,
      dispatch,
      DocumentType.notice_board_meeting,
      this.documentLanguage.value,
    );
  }

  init(): CleanupFunction {
    const cleanupFunctions = [autorun(() => this.handleLanguageChanges())];

    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }
  get locale(): Language {
    return this.context.locale;
  }

  get roles(): ProjectCompanyRole[] {
    return this.context.projectRoles;
  }

  get meetingForm(): RadioFieldModel<BoardMeetingForm> {
    return this.context.boardMeetingFields.boardMeetingForm;
  }

  get meetingPlace(): RadioFieldModel<MeetingPlace> {
    return this.context.boardMeetingFields.meetingPlace;
  }

  get inlineAddress(): StringFieldModel {
    return this.context.boardMeetingFields.inlineAddress;
  }

  get boardMeetingNumber(): StringFieldModel {
    return this.context.boardMeetingFields.boardMeetingNumber;
  }

  get meetingStartDate(): LocalDateFieldModel {
    return this.context.boardMeetingFields.meetingStartDate;
  }

  get meetingStartTime(): TimeFieldModel {
    return this.context.boardMeetingFields.meetingStartTime;
  }

  get meetingEndTime(): TimeFieldModel {
    return this.context.boardMeetingFields.meetingEndTime;
  }

  get documentId(): string {
    const noticeDocument = this.context.currentDocuments.find(
      (d) => d.type === DocumentType.notice_board_meeting,
    );
    return noticeDocument?.documentId ?? "n/a";
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get boardMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isBoardMember);
  }

  get deputyMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isDeputyBoardMember);
  }

  get observers(): ProjectCompanyRole[] {
    return this.roles.filter(isObserver);
  }

  get hasDeputyBoardMembers(): boolean {
    return this.deputyMembers.length > 0;
  }

  get hasObservers(): boolean {
    return this.observers.length > 0;
  }

  get currentDecisions(): DecisionItem[] {
    return this.context.currentDecisions;
  }

  get showAddress(): boolean {
    return (
      this.meetingForm.value === BoardMeetingForm.HYBRID ||
      this.meetingForm.value === BoardMeetingForm.PHYSICAL
    );
  }

  get showInlineAddress(): boolean {
    return this.showAddress && this.meetingPlace.value === MeetingPlace.inlineAddress;
  }

  get showElectronicLink(): boolean {
    return (
      this.meetingForm.value === BoardMeetingForm.HYBRID ||
      this.meetingForm.value === BoardMeetingForm.ELECTRONIC
    );
  }

  get showElectronicLinkInput(): boolean {
    return (
      this.showElectronicLink &&
      this.meetingLinkOption.value === NoticeBoardMeetingLinkOption.linkGiven
    );
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/decision-added":
        this.initDecisionTextModel(event.decisionId);
        break;
      case "dbot/upload-attachments-result":
        if (event.source === this && isOk(event.result)) {
          this.isUploadingAttachments = false;
          event.result.data.forEach((attachment) => {
            this.attachments.set(attachment.id, attachment);
          });
        }
        break;
      case "dbot/delete-attachment-result":
        if (event.source === this) {
          this.removeAttachment(event.attachmentId);
        }
        break;
      case "dbot/update-board-meeting-recipients-result":
        this.updateBoardMeetingNoticeRecipients(event);
        break;
    }
  }

  uploadAttachments(files: File[]) {
    if (!files.length) {
      return;
    }

    this.isUploadingAttachments = true;
    this.dispatch(
      uploadAttachmentsEvent({
        source: this,
        files: Array.from(files),
        reference: this.documentId,
        attachmentType: AttachmentType.UPLOADED_FILE,
      }),
    );
  }

  openAttachment(attachmentId: string) {
    this.dispatch({ type: "dbot/open-attachment", attachmentId });
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.context.fileUploadConfig;
  }

  removeAttachment(attachmentId: string) {
    this.attachments.delete(attachmentId);
  }

  deleteAttachmentById(attachmentId: string) {
    this.dispatch(deleteAttachmentEvent(attachmentId, this));
  }

  getUploadedAttachment() {
    return Array.from(this.attachments.entries());
  }

  updateBoardMeetingNoticeRecipients(event: UpdateBoardMeetingRecipientsResultEvent) {
    if (isOk(event.result)) {
      this.board = updateParticipants(this.board, event.result.data);
      this.extraParticipants = updateParticipants(this.extraParticipants, event.result.data);
    }
  }

  addExtraParticipant() {
    this.extraParticipantFormModel = {
      nameField: new StringFieldModel({ required: true }),
      emailField: createEmailField({ required: true }),
    };
  }

  cancelAddExtraParticipant() {
    this.extraParticipantFormModel = undefined;
  }

  submitExtraParticipant() {
    if (!this.extraParticipantFormModel) {
      return;
    }
    const { nameField, emailField } = this.extraParticipantFormModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.extraParticipants.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      nameField.clear();
      emailField.clear();
      nameField.focus();
    }
  }

  removeExtraParticipant(participant: ExtraParticipant) {
    this.extraParticipants.splice(this.extraParticipants.indexOf(participant), 1);
  }

  get openSendNoticeDialogAction() {
    return openSendNoticeDialogAction({
      document: this.documentItem,
      sendingTime: this.documentStatus?.sentAt,
      isBatchProject: this.context.isBatchProject,
    });
  }

  get openNotifyNoticeChangeDialogAction() {
    return openNotifyNoticeChangeDialogAction({
      document: this.documentItem,
      sendingTime: this.documentStatus?.sentAt,
    });
  }

  initDecisionTextModel(decisionId: string) {
    const decisionTextModel = this.decisionTextModels.find((d) => d.decisionId === decisionId);
    if (decisionTextModel === undefined) {
      const decision = this.context.currentDecisions.find((d) => d.decisionId === decisionId);
      const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
      if (decision) {
        const model = mapDecisionTextModel(
          this.dispatch,
          decision,
          DocumentType.notice_board_meeting,
          this.context,
          documentLanguages,
        );
        this.decisionTextModels.push(model);
      }
    }
  }

  get noticeDescription(): LocalizedText[] {
    return this.context.jsonData.boardMeeting?.noticeDescription ?? [];
  }

  get noticeDescriptionModel(): LocalizedTextEditorModel {
    return this.context.boardMeetingFields.noticeDescription;
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (isLanguageChanged(documentLanguages, this.noticeDescriptionModel.textModels)) {
      runInAction(() => {
        this.noticeDescriptionModel.onLanguageChange(documentLanguages, this.noticeDescription);
        this.decisionTextModels = mapLocalizedDecisionTextsToModel(
          this.context,
          this.dispatch,
          DocumentType.notice_board_meeting,
          this.documentLanguage.value,
        );
      });
    }
  }
}

function updateParticipants(
  current: ProjectCompanyRole[],
  updates: UpdateBoardMeetingRecipientItem[],
): ProjectCompanyRole[];
function updateParticipants(
  current: ExtraParticipant[],
  updates: UpdateBoardMeetingRecipientItem[],
): ExtraParticipant[];
function updateParticipants<T extends ProjectCompanyRole | ExtraParticipant>(
  currentOptions: T[],
  updates: UpdateBoardMeetingRecipientItem[],
): T[] {
  return currentOptions.map((option) => {
    const id = "companyroleId" in option ? option.companyroleId : option.id;
    const update = updates.find((u) => u.id === id);
    if (update) {
      return {
        ...option,
        email: update.email,
      };
    }
    return option;
  });
}

export function createMeetingLinkOptionField(
  linkOption: NoticeBoardMeetingLinkOption,
): RadioFieldModel<NoticeBoardMeetingLinkOption> {
  return new RadioFieldModel<NoticeBoardMeetingLinkOption>({
    value: linkOption,
    options: Object.values(NoticeBoardMeetingLinkOption),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case NoticeBoardMeetingLinkOption.linkGiven:
          return { key: "documents.meetingForm.electronicParticipationLinkGiven" };
        case NoticeBoardMeetingLinkOption.linkGivenLater:
          return { key: "documents.meetingForm.electronicParticipationLinkGivenLater" };
      }
    },
  });
}

export function applyForm(form: NoticeOfBoardMeetingForm): NoticeOfBoardMeeting {
  return {
    type: DocumentType.notice_board_meeting,
    dateNoticeBoardMeeting: form.dateNoticeBoardMeeting.toISOString(),
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
    attachments: Array.from(form.attachments.values()),
    meetingForm: form.meetingForm.value,
    meetingPlace: form.meetingPlace.value,
    meetingLinkOption: form.meetingLinkOption.value,
    inlineAddress: form.inlineAddress.validatedValue,
    inlineMeetingLink: form.inlineMeetingLink.validatedValue,
    board: form.board.slice(),
    participants: getSelectedBoardRoleIds(form),
    extraParticipants: form.extraParticipants.slice(),
  };
}

export enum NoticeOfBoardMeetingCustomPanel {
  meetingDetails = "meetingDetails",
  recipients = "recipients",
}

export function getNoticeOfBoardMeetingCustomPanels(
  form: NoticeOfBoardMeetingForm,
): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: NoticeOfBoardMeetingCustomPanel.meetingDetails,
      documentId: form.documentId,
      icon: "calendar",
      heading: { key: "dbot.meeting-details" },
      label: {
        translated: formatDateAndTime(
          form.locale,
          form.meetingStartDate.validatedValue,
          form.meetingStartTime.validatedValue,
        ),
      },
    },
    {
      type: "custom",
      id: NoticeOfBoardMeetingCustomPanel.recipients,
      documentId: form.documentId,
      icon: "users",
      heading: { key: "dbot.recipients" },
      label: boardMeetingParticipantsLabel({
        board: form.boardMembers,
        participants: form.selectedBoardMembers.selected
          .concat(form.selectedDeputyMembers.selected)
          .map((m) => m.companyroleId),
        extraParticipants: form.extraParticipants,
      }),
    },
  ];
}
